/**
 * @module api/domain/partnershipInquiry
 * @description 고객사·협력사 신청(리드) 관리자 API — customer-back `/admin/partnership-inquiries`.
 *   조회 2건 + 처리 2건 + 대기 건수 1건. 모두 customer-back 전용 인스턴스를 사용한다(cs-back 아님).
 */
import { customerGetApi, customerPostApi, customerSilentGetApi } from '@/api/customerApi'
import { buildQueryString } from '@/mixin/mixins'

/** customer-back 관리자 리드 API 접두어 (baseURL 이 `/app/v1` 까지 갖는다) */
const BASE_URL = '/admin/partnership-inquiries'

/**
 * 신청 목록을 페이지네이션으로 조회합니다.
 * `status` 가 비어 있으면 파라미터 자체를 보내지 않습니다(서버 기준 = 전체).
 *
 * @param {number} page - 페이지 번호 (0-based)
 * @param {number} size - 페이지당 행 수
 * @param {{status?: string}} [queryObject] - 검색 조건
 * @returns {Promise<{status: number|null, data: any}>} content, currentPage, totalPages, totalElements
 */
export const getPartnershipInquiryGrid = async (page, size, queryObject) => {
  const params = { page, size }
  if (queryObject && queryObject.status) {
    params.status = queryObject.status
  }
  return await customerGetApi(BASE_URL + buildQueryString(params))
}

/**
 * 신청 상세를 조회합니다.
 *
 * @param {number|string} inquiryId - 신청 ID
 * @returns {Promise<{status: number|null, data: any}>}
 */
export const getPartnershipInquiryDetail = async (inquiryId) => {
  return await customerGetApi(`${BASE_URL}/${inquiryId}`)
}

/**
 * 신청을 승인합니다. `REVIEWING` 이 아니면 409 를 돌려받습니다.
 *
 * @param {number|string} inquiryId - 신청 ID
 * @returns {Promise<{status: number|null, data: any}>}
 */
export const approvePartnershipInquiry = async (inquiryId) => {
  return await customerPostApi(`${BASE_URL}/${inquiryId}/approve`)
}

/**
 * 신청을 반려합니다. 사유는 필수이며 최대 500자입니다.
 *
 * @param {number|string} inquiryId - 신청 ID
 * @param {string} reason - 반려 사유
 * @returns {Promise<{status: number|null, data: any}>}
 */
export const rejectPartnershipInquiry = async (inquiryId, reason) => {
  return await customerPostApi(`${BASE_URL}/${inquiryId}/reject`, { reason })
}

/**
 * 검토 중(대기) 건수를 조회합니다. 메뉴 배지 전용이라 실패해도 세션을 만료시키지 않습니다.
 *
 * @returns {Promise<{status: number|null, data: any}>} `{ pendingCount: number }`
 */
export const getPartnershipPendingCount = async () => {
  return await customerSilentGetApi(`${BASE_URL}/pending-count`)
}
